import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import NumberField from "../common/NumberField";

type CampaignMarginPeriod = components["schemas"]["CampaignMarginPeriod"];
type CreateMarginPeriodInput = components["schemas"]["CreateMarginPeriodInput"];

interface MarginDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (marginData: CreateMarginPeriodInput) => Promise<void>;
  margin?: CampaignMarginPeriod;
  isLoading: boolean;
  title: string;
  campaignStartDate?: string | null;
  campaignEndDate?: string | null;
}

const MarginDialog: React.FC<MarginDialogProps> = ({
  open,
  onClose,
  onSave,
  margin,
  isLoading,
  title,
  campaignStartDate,
  campaignEndDate,
}) => {
  const { t } = useTranslation();
  const [marginValue, setMarginValue] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [startDateDayjs, setStartDateDayjs] = useState<dayjs.Dayjs | null>(
    null
  );
  const [endDateDayjs, setEndDateDayjs] = useState<dayjs.Dayjs | null>(null);
  const [errors, setErrors] = useState<{
    margin?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  const normalizeDateString = (date?: string | null) => {
    if (!date) return null;
    const [datePart] = date.split("T");
    return datePart;
  };

  const normalizedCampaignStartDate = normalizeDateString(campaignStartDate);
  const normalizedCampaignEndDate = normalizeDateString(campaignEndDate);

  useEffect(() => {
    if (margin) {
      const marginStartDate = normalizeDateString(margin.startDate) || "";
      const marginEndDate = normalizeDateString(margin.endDate) || "";
      setMarginValue(margin.margin.toString());
      setStartDate(marginStartDate);
      setEndDate(marginEndDate);
      setStartDateDayjs(marginStartDate ? dayjs(marginStartDate) : null);
      setEndDateDayjs(marginEndDate ? dayjs(marginEndDate) : null);
    } else {
      setMarginValue("");
      setStartDate("");
      setEndDate("");
      setStartDateDayjs(null);
      setEndDateDayjs(null);
    }
    setErrors({});
  }, [margin, open]);

  const validateForm = (): boolean => {
    const newErrors: {
      margin?: string;
      startDate?: string;
      endDate?: string;
    } = {};

    if (!marginValue.trim()) {
      newErrors.margin = t("common.required", "Required");
    } else if (isNaN(parseFloat(marginValue)) || parseFloat(marginValue) < 0) {
      newErrors.margin = t(
        "common.invalidNumber",
        "Please enter a valid positive number"
      );
    }

    if (!startDate) {
      newErrors.startDate = t("common.required", "Required");
    }

    if (!endDate) {
      newErrors.endDate = t("common.required", "Required");
    } else if (
      startDate &&
      dayjs(endDate).startOf("day").isBefore(dayjs(startDate).startOf("day"))
    ) {
      newErrors.endDate = t(
        "common.endDateAfterStart",
        "End date must be after or equal to start date"
      );
    }

    // Validate against campaign date range if provided
    const campaignStartDay = normalizedCampaignStartDate
      ? dayjs(normalizedCampaignStartDate).startOf("day")
      : null;
    const campaignEndDay = normalizedCampaignEndDate
      ? dayjs(normalizedCampaignEndDate).startOf("day")
      : null;
    const startDay = startDate ? dayjs(startDate).startOf("day") : null;
    const endDay = endDate ? dayjs(endDate).startOf("day") : null;

    if (campaignStartDay && startDay && startDay.isBefore(campaignStartDay)) {
      newErrors.startDate = t(
        "common.startDateTooEarly",
        "Start date cannot be before campaign start date ({{date}})",
        { date: campaignStartDay.format("L") }
      );
    }

    if (campaignEndDay && endDay && endDay.isAfter(campaignEndDay)) {
      newErrors.endDate = t(
        "common.endDateTooLate",
        "End date cannot be after campaign end date ({{date}})",
        { date: campaignEndDay.format("L") }
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Don't use try-catch here, let the parent component handle errors
    // This ensures that the error state is properly set in the RTK Query hooks
    onSave({
      margin: parseFloat(marginValue),
      startDate: startDateDayjs
        ? startDateDayjs.format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      endDate: endDateDayjs
        ? endDateDayjs.format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
    });
    // The dialog will be closed by the parent component if the operation succeeds
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <NumberField
            label={t("metrics.marginTable.margin", "Margin") + " (%)"}
            value={marginValue}
            onChange={(e) => setMarginValue(e.target.value)}
            fullWidth
            inputProps={{ step: "0.01" }}
            error={!!errors.margin}
            helperText={errors.margin}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t("metrics.marginTable.startDate", "Start Date")}
              value={startDateDayjs}
              onChange={(newDate: dayjs.Dayjs | null) => {
                setStartDateDayjs(newDate);
                if (newDate) {
                  setStartDate(newDate.format("YYYY-MM-DD"));
                } else {
                  setStartDate("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  sx={{ width: "100%" }}
                />
              )}
              minDate={
                normalizedCampaignStartDate
                  ? dayjs(normalizedCampaignStartDate)
                  : undefined
              }
              maxDate={
                normalizedCampaignEndDate
                  ? dayjs(normalizedCampaignEndDate)
                  : undefined
              }
              disableMaskedInput
            />
            <Box sx={{ mt: 2 }}>
              <DatePicker
                label={t("metrics.marginTable.endDate", "End Date")}
                value={endDateDayjs}
                onChange={(newDate: dayjs.Dayjs | null) => {
                  setEndDateDayjs(newDate);
                  if (newDate) {
                    setEndDate(newDate.format("YYYY-MM-DD"));
                  } else {
                    setEndDate("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                  />
                )}
                minDate={
                  startDateDayjs ??
                  (normalizedCampaignStartDate
                    ? dayjs(normalizedCampaignStartDate)
                    : undefined)
                }
                maxDate={
                  normalizedCampaignEndDate
                    ? dayjs(normalizedCampaignEndDate)
                    : undefined
                }
                disableMaskedInput
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          color="primary"
        >
          {t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarginDialog;
